import { useState, useEffect } from "react";

// hook to save and load values from localstorage
//
// usage:
//   const [value, setValue] = localString('myKey');
//   const [value, setValue] = localString('myKey', 'default');
//   const [obj, setObj] = useLocalObject<MyType>('myKey');
//   const [obj, setObj] = useLocalObject<MyType>('myKey', { id: 1 });
//   setObj((prev) => ({ ...prev, id: 2 }));

export function useLocalString(
  key: string,
  defaultValue?: string,
): [string | null, (value: string | ((prev: string | null) => string)) => void] {
  const [value, setValue] = useState<string | null>(() => {
    try {
      return localStorage.getItem(key) ?? defaultValue ?? null;
    } catch {
      return defaultValue ?? null;
    }
  });

  const setValueAndStorage = (newValue: string | ((prev: string | null) => string)): void => {
    setValue((prev) => {
      const resolved =
        typeof newValue === "function"
          ? (newValue as (prev: string | null) => string)(prev)
          : newValue;
      try {
        localStorage.setItem(key, resolved);
      } catch {
        // silently fail if localstorage is unavailable
      }
      return resolved;
    });
  };

  return [value, setValueAndStorage];
}

export function useLocalNumber(
  key: string,
  defaultValue?: number,
): [number | null, (value: number | ((prev: number | null) => number)) => void] {
  const [value, setValue] = useState<number | null>(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? Number(item) : (defaultValue ?? null);
    } catch {
      return defaultValue ?? null;
    }
  });

  const setValueAndStorage = (newValue: number | ((prev: number | null) => number)): void => {
    setValue((prev) => {
      const resolved =
        typeof newValue === "function"
          ? (newValue as (prev: number | null) => number)(prev)
          : newValue;
      try {
        localStorage.setItem(key, String(resolved));
      } catch {
        // silently fail if localstorage is unavailable
      }
      return resolved;
    });
  };

  return [value, setValueAndStorage];
}

export function useLocalObject<T extends Record<string, any>>(
  key: string,
  defaultValue?: T,
): [T | null, (value: T | ((prev: T | null) => T)) => void] {
  const [value, setValue] = useState<T | null>(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : (defaultValue ?? null);
    } catch {
      return defaultValue ?? null;
    }
  });

  const setValueAndStorage = (newValue: T | ((prev: T | null) => T)): void => {
    setValue((prev) => {
      const resolved =
        typeof newValue === "function" ? (newValue as (prev: T | null) => T)(prev) : newValue;
      try {
        localStorage.setItem(key, JSON.stringify(resolved));
      } catch {
        // silently fail if localstorage is unavailable
      }
      return resolved;
    });
  };

  return [value, setValueAndStorage];
}
